import React, { useState, useMemo } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  SURVEY_SERVICES,
  SURVEY_PROVIDERS,
  SURVEY_PLACEMENT_METHODS,
  SURVEY_NEEDS_ADDRESSED,
  SURVEY_NEED_DETAILS,
} from "../../../ViewUserPage/components/constants";
import "./index.css";

const SECTION_CONFIG = [
  { key: "services", title: "Services Provided", items: SURVEY_SERVICES },
  { key: "providers", title: "Service Providers", items: SURVEY_PROVIDERS },
  { key: "placementMethods", title: "Placement Methods", items: SURVEY_PLACEMENT_METHODS },
  { key: "needsAddressed", title: "Needs Addressed", items: SURVEY_NEEDS_ADDRESSED },
];

const countChecked = (obj) => {
  if (!obj) return 0;
  return Object.values(obj).filter(Boolean).length;
};

const ResourceMatching = ({ cachedClassrooms }) => {
  const [expandedId, setExpandedId] = useState(null);

  const classroomsWithSurvey = useMemo(() => {
    if (!cachedClassrooms) return [];
    return Object.entries(cachedClassrooms)
      .filter(([, c]) => c.programSurvey)
      .map(([id, c]) => ({
        id,
        className: c.className || "Unnamed Classroom",
        countyName: c.countyName || "",
        survey: c.programSurvey,
        updatedAt: c.programSurvey.updatedAt,
        teachers: c.teachers
          ? Object.values(c.teachers).map((t) => t.name || t.email).filter(Boolean)
          : [],
        submittedBy: c.programSurvey.submittedBy || null,
        servicesCount: countChecked(c.programSurvey.services),
        providersCount: countChecked(c.programSurvey.providers),
        placementCount: countChecked(c.programSurvey.placementMethods),
        needsCount: countChecked(c.programSurvey.needsAddressed),
      }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [cachedClassrooms]);

  const totalClassrooms = cachedClassrooms ? Object.keys(cachedClassrooms).length : 0;
  const completedCount = classroomsWithSurvey.length;

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const formatDate = (ts) => {
    if (!ts) return "N/A";
    return new Date(ts).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="resource-matching-admin">
      <div className="resource-matching-admin__summary">
        <div className="resource-matching-admin__stat">
          <span className="resource-matching-admin__stat-value">{completedCount}</span>
          <span className="resource-matching-admin__stat-label">Surveys Completed</span>
        </div>
        <div className="resource-matching-admin__stat">
          <span className="resource-matching-admin__stat-value">{totalClassrooms - completedCount}</span>
          <span className="resource-matching-admin__stat-label">Surveys Pending</span>
        </div>
        <div className="resource-matching-admin__stat">
          <span className="resource-matching-admin__stat-value">
            {totalClassrooms > 0 ? Math.round((completedCount / totalClassrooms) * 100) : 0}%
          </span>
          <span className="resource-matching-admin__stat-label">Completion Rate</span>
        </div>
      </div>

      {completedCount === 0 ? (
        <div className="resource-matching-admin__empty">
          No classrooms have completed the Program Intervention Survey yet.
        </div>
      ) : (
        <div className="resource-matching-admin__table-container">
          <table className="resource-matching-admin__table">
            <thead>
              <tr>
                <th></th>
                <th>Classroom</th>
                <th>County</th>
                <th>Coordinators</th>
                <th>Submitted By</th>
                <th>Services</th>
                <th>Providers</th>
                <th>Placement</th>
                <th>Needs Addressed</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {classroomsWithSurvey.map((c) => (
                <React.Fragment key={c.id}>
                  <tr
                    className={`resource-matching-admin__row ${expandedId === c.id ? "resource-matching-admin__row--expanded" : ""}`}
                    onClick={() => toggleExpand(c.id)}
                  >
                    <td className="resource-matching-admin__expand-cell">
                      {expandedId === c.id ? (
                        <UpOutlined className="resource-matching-admin__expand-icon" />
                      ) : (
                        <DownOutlined className="resource-matching-admin__expand-icon" />
                      )}
                    </td>
                    <td className="resource-matching-admin__name-cell">{c.className}</td>
                    <td>{c.countyName}</td>
                    <td>
                      {c.teachers.length > 0
                        ? c.teachers.join(", ")
                        : <span className="resource-matching-admin__muted">None assigned</span>}
                    </td>
                    <td>
                      {c.submittedBy
                        ? <span>{c.submittedBy.displayName || c.submittedBy.email}</span>
                        : <span className="resource-matching-admin__muted">Unknown</span>}
                    </td>
                    <td>
                      <span className="resource-matching-admin__count-badge">
                        {c.servicesCount}/{SURVEY_SERVICES.length}
                      </span>
                    </td>
                    <td>
                      <span className="resource-matching-admin__count-badge">
                        {c.providersCount}/{SURVEY_PROVIDERS.length}
                      </span>
                    </td>
                    <td>
                      <span className="resource-matching-admin__count-badge">
                        {c.placementCount}/{SURVEY_PLACEMENT_METHODS.length}
                      </span>
                    </td>
                    <td>
                      <span className="resource-matching-admin__count-badge">
                        {c.needsCount}/{SURVEY_NEEDS_ADDRESSED.length}
                      </span>
                    </td>
                    <td>{formatDate(c.updatedAt)}</td>
                  </tr>
                  {expandedId === c.id && (
                    <tr className="resource-matching-admin__detail-row">
                      <td colSpan={10}>
                        <div className="resource-matching-admin__detail">
                          {SECTION_CONFIG.map(({ key, title, items }) => (
                            <div key={key} className="resource-matching-admin__section">
                              <h4 className="resource-matching-admin__section-title">{title}</h4>
                              <div className="resource-matching-admin__checklist">
                                {items.map(({ key: itemKey, label }) => {
                                  const checked = c.survey[key]?.[itemKey] || false;
                                  const pickedDetails = key === "needsAddressed" && checked
                                    ? (SURVEY_NEED_DETAILS[itemKey] || []).filter(
                                        (d) => c.survey.needDetails?.[itemKey]?.[d.key]
                                      )
                                    : [];
                                  return (
                                    <div key={itemKey}>
                                      <div
                                        className={`resource-matching-admin__check-item ${checked ? "resource-matching-admin__check-item--checked" : ""}`}
                                      >
                                        {checked ? (
                                          <CheckCircleOutlined className="resource-matching-admin__check-icon--yes" />
                                        ) : (
                                          <CloseCircleOutlined className="resource-matching-admin__check-icon--no" />
                                        )}
                                        <span>{label}</span>
                                      </div>
                                      {pickedDetails.length > 0 && (
                                        <ul className="resource-matching-admin__sub-checklist">
                                          {pickedDetails.map((d) => (
                                            <li key={d.key}>{d.label}</li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ResourceMatching;
